import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { pget, ppost, pdel, fmtDate, fmtTime } from "./portalApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarCheck, Users, Clock, X, Calendar as CalendarIcon } from "lucide-react";

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const minOf = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + (m || 0); };
const hhmm = (v) => `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;

export default function PortalAmenities() {
  const [amenities, setAmenities] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null); // amenity being booked
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [calOpen, setCalOpen] = useState(false);
  const [party, setParty] = useState(1);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([pget("/resident/amenities"), pget("/resident/reservations")])
      .then(([a, r]) => { setAmenities(a.data.items || []); setMine(r.data.items || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openBooking = (a) => {
    const today = new Date(); today.setDate(today.getDate() + 1);
    setBooking(a); setSlot(""); setParty(1);
    setDate(today.toISOString().slice(0, 10));
  };

  // compute free slots whenever date changes
  useEffect(() => {
    if (!booking || !date) { setSlots([]); return; }
    const av = booking.availability || {};
    const dayName = DAY_KEYS[new Date(`${date}T12:00:00`).getDay()];
    if ((av.days || []).length && !av.days.includes(dayName)) { setSlots([{ closed: true }]); return; }
    const open = minOf(av.open_time || "08:00"), close = minOf(av.close_time || "21:00");
    const step = parseInt(av.slot_minutes) || 60;
    pget(`/resident/amenities/${booking.id}/day?date=${date}`).then((r) => {
      const booked = r.data.booked || [];
      const out = [];
      for (let t = open; t + step <= close; t += step) {
        const s = hhmm(t), e = hhmm(t + step);
        const taken = booked.some((b) => minOf(b.start_time) < minOf(e) && minOf(s) < minOf(b.end_time || hhmm(minOf(b.start_time) + step)));
        out.push({ start: s, end: e, taken });
      }
      setSlots(out);
    }).catch(() => setSlots([]));
  }, [booking, date]);

  const book = async () => {
    if (!slot) { toast.error("Pick a time slot"); return; }
    setSaving(true);
    try {
      const r = await ppost("/resident/reservations", { amenity: booking.name, date, start_time: slot, party_size: party });
      toast.success(r.data.needs_approval ? "Request sent — management will confirm shortly ✅" : "Booked! See you there 🎉");
      setBooking(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't book that slot"); }
    finally { setSaving(false); }
  };

  const cancel = async (r) => {
    try { await pdel(`/resident/reservations/${r.id}`); toast.success("Reservation cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel"); }
  };

  return (
    <div className="space-y-6" data-testid="portal-amenities-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><CalendarCheck className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Amenities</h1>
        <p className="text-muted-foreground mt-1">Book the clubhouse, pool and more — right here or by texting the concierge.</p>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        : amenities.length === 0 ? <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground">This community hasn&apos;t opened amenity booking yet.</div>
        : <div className="grid gap-3 sm:grid-cols-2" data-testid="portal-amenities-list">
          {amenities.map((a) => {
            const av = a.availability || {};
            return (
              <div key={a.id} data-testid={`portal-amenity-${a.id}`} className="bg-white rounded-2xl border border-sand-200 p-5 flex flex-col">
                <div className="font-heading font-bold text-ink">{a.name}</div>
                {a.description && <p className="text-sm text-ink-600 mt-1">{a.description}</p>}
                <div className="text-xs text-muted-foreground mt-2 space-y-1">
                  {av.open_time && <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {(av.days || []).join(", ")} · {fmtTime(av.open_time)}–{fmtTime(av.close_time)}</div>}
                  {a.capacity ? <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Up to {a.capacity} people</div> : null}
                  {a.approval_required && <div className="text-amber-700">Bookings need management approval</div>}                </div>
                <button data-testid={`portal-book-${a.id}`} onClick={() => openBooking(a)}
                  className="mt-4 rounded-full bg-redline-500 text-white font-semibold px-4 py-2 text-sm hover:bg-redline-600 transition-colors self-start">Book a time</button>
              </div>
            );
          })}
        </div>}

      <section data-testid="portal-my-reservations">
        <h2 className="font-heading font-bold text-ink text-sm uppercase tracking-wide mb-2">My upcoming reservations</h2>
        {mine.length === 0 ? <p className="text-sm text-muted-foreground">Nothing booked yet.</p>
          : <div className="space-y-2">
            {mine.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-sand-200 p-4 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink">{r.amenity_name}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(r.date, { weekday: "short", month: "short", day: "numeric" })} · {fmtTime(r.start_time)}–{fmtTime(r.end_time)} · party of {r.party_size}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.status === "approved" ? "Confirmed" : "Awaiting approval"}</span>
                <button data-testid={`portal-cancel-resv-${r.id}`} onClick={() => cancel(r)} title="Cancel" className="h-8 w-8 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-danger-600 flex items-center justify-center transition-colors"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>}
      </section>

      <Dialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)}>
        <DialogContent className="max-w-md" data-testid="portal-booking-dialog">
          {booking && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Book {booking.name}</DialogTitle><DialogDescription>{booking.approval_required ? "Management confirms each booking." : "Free slots book instantly."}</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><label className="text-sm font-semibold">Date</label>
                  {/* iter252/L7 — a raw native date field looked out of place in the portal and
                      was fiddly on mobile. Same shadcn Calendar the dashboard uses. */}
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button data-testid="portal-book-date" type="button"
                        className="mt-1 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm text-left outline-none focus:ring-2 focus:ring-redline-500/30 flex items-center gap-2 hover:bg-sand-50 transition-colors">
                        <CalendarIcon className="h-4 w-4 text-ink/40" strokeWidth={2} />
                        {date ? new Date(`${date}T00:00:00`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={date ? new Date(`${date}T00:00:00`) : undefined}
                        disabled={{ before: new Date() }}
                        onSelect={(d) => {
                          if (!d) return;
                          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          setDate(iso); setSlot(""); setCalOpen(false);
                        }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm font-semibold">Time</label>
                  {slots.length === 1 && slots[0].closed ? <p className="text-sm text-amber-700 mt-1">Closed that day — try another date.</p>
                    : <div className="mt-1 grid grid-cols-3 gap-2 max-h-44 overflow-y-auto">
                      {slots.map((s) => (
                        <button key={s.start} data-testid={`portal-slot-${s.start}`} disabled={s.taken} onClick={() => setSlot(s.start)}
                          className={`rounded-xl px-2 py-2 text-xs font-semibold border transition-colors ${s.taken ? "bg-sand-100 text-sand-400 border-sand-100 cursor-not-allowed line-through" : slot === s.start ? "bg-redline-500 text-white border-redline-500" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>
                          {fmtTime(s.start)}
                        </button>
                      ))}
                    </div>}
                </div>
                <div><label className="text-sm font-semibold">Party size</label>
                  <input data-testid="portal-book-party" type="number" min="1" max={booking.capacity || 50} value={party} onChange={(e) => setParty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="mt-1 w-24 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" /></div>
              </div>
              <DialogFooter>
                <button onClick={() => setBooking(null)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
                <button data-testid="portal-book-submit" disabled={saving || !slot} onClick={book} className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">{saving ? "Booking…" : "Book it"}</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
